import type { Project, Category } from "~/types/projects";
import type { ClientData } from "~/types/clients";
import type { AcfOptions, SiteOptions } from "~/types/options";

export const useWordpressApi = () => {

  const decodeHtmlEntities = (text: string): string => {
    return text
      .replace(/&amp;/g, '&')
      .replace(/&#038;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&#8217;/g, "'")
      .replace(/&#8216;/g, "'")
      .replace(/&#8211;/g, '–')
      .replace(/&#8212;/g, '—')
      .replace(/&nbsp;/g, ' ')
      .replace(/&#160;/g, ' ');
  };

  /**
   * @description Récupère tous les projets 
   */
const getProjects = async () => {
  const config = useRuntimeConfig();
  
  try {
    // Fetch des projets depuis WordPress
const projects: any[] = await $fetch(
  `${config.public.wpApiBase}/projects?per_page=100&_fields=id,title,slug,acf,featured_media,categories`,
  { timeout: 30000 }
);

    const mediaIds = projects
      .map(p => p.featured_media)
      .filter((id): id is number => typeof id === 'number' && id > 0);
    let mediaMap = new Map<number, string>();
    if (mediaIds.length > 0) {
      const medias: any[] = await $fetch(
        `${config.public.wpApiBase}/media?per_page=100&include=${mediaIds.join(',')}&_fields=id,source_url`,
        { timeout: 30000 }
      );
      mediaMap = new Map(medias.map(m => [m.id, m.source_url]));
    }
    return projects.map(project => ({
      ...project,
      title: { rendered: decodeHtmlEntities(project.title.rendered) },
      featured_media_url: mediaMap.get(project.featured_media) || null
    }));

  } catch (err) {
    console.error("Erreur lors de la récupération des projets :", err);
    return [];
  }
};

  /**
   * @description Récupère les 3 derniers projets
   */
const getLastProjects = async () => {
  const config = useRuntimeConfig();
  
  try {
    const projects: any[] = await $fetch(
      `${config.public.wpApiBase}/projects?per_page=3&_fields=id,title,slug,acf,featured_media,categories`,
      { timeout: 30000 }
    );

    const mediaIds = projects
      .map(p => p.featured_media)
      .filter((id): id is number => typeof id === 'number' && id > 0);

    let mediaMap = new Map<number, string>();
    if (mediaIds.length > 0) {
      const medias: any[] = await $fetch(
        `${config.public.wpApiBase}/media?include=${mediaIds.join(',')}&_fields=id,source_url`,
        { timeout: 30000 }
      );
      mediaMap = new Map(medias.map(m => [m.id, m.source_url]));
    }

    return projects.map(project => ({
      ...project,
      title: { rendered: decodeHtmlEntities(project.title.rendered) },
      featured_media_url: mediaMap.get(project.featured_media) || null
    }));

  } catch (err) {
    console.error("Erreur lors de la récupération des derniers projets :", err);
    return [];
  }
};

/**
 * @description Récupère 3 projets d'une catégorie donnée
 * @param categoryId - L'id de la catégorie
 * @param excludeId - (optionnel) id d'un projet à exclure (ex: le projet courant)
 */
const getProjectsByCategory = async (categoryId: number, excludeId?: number) => {
  const config = useRuntimeConfig();

  try {
    const exclude = excludeId ? `&exclude=${excludeId}` : '';

    const projects: any[] = await $fetch(
      `${config.public.wpApiBase}/projects?categories=${categoryId}&per_page=3${exclude}&_fields=id,title,slug,featured_media`,
      { timeout: 30000 }
    );

    const mediaIds = projects
      .map(p => p.featured_media)
      .filter((id): id is number => typeof id === 'number' && id > 0);

    let mediaMap = new Map<number, string>();
    if (mediaIds.length > 0) {
      const medias: any[] = await $fetch(
        `${config.public.wpApiBase}/media?include=${mediaIds.join(',')}&_fields=id,source_url`,
        { timeout: 30000 }
      );
      mediaMap = new Map(medias.map(m => [m.id, m.source_url]));
    }

    return projects.map(project => ({
      id: project.id,
      slug: project.slug,
      title: { rendered: decodeHtmlEntities(project.title.rendered) },
      featured_media_url: mediaMap.get(project.featured_media) || null
    }));

  } catch (err) {
    console.error("Erreur lors de la récupération des projets par catégorie :", err);
    return [];
  }
};

  /**
   * @description Récupère un projet en fonction de l'id
   * @param id : String
   */
  const getProjectById = async (id: string): Promise<Project | null> => {
    const config = useRuntimeConfig();
    try {
      const data = await $fetch<Project>(
        `${config.public.wpApiBase}/projects/${id}`
      );
      
      // Décode le titre
      if (data.title?.rendered) {
        data.title.rendered = decodeHtmlEntities(data.title.rendered);
      }
      
      return data;
    } catch (err: any) {
      console.error("Erreur fetch projet:", err);
      return null;
    }
  };

  /**
   * @description Récupère les catégories via l'API Nuxt (avec cache)
   */
  const getCategories = async () => {
    try {
      const data = await $fetch<Category[]>('/api/categories');
      
      // Décode les noms
      return (data ?? []).map(category => ({
        ...category,
        name: decodeHtmlEntities(category.name)
      }));
    } catch (err: any) {
      console.error("Erreur dans la récupération des catégories", err);
      return [];
    }
  };

  /**
   * @description Récupère les clients pour la hp
   */
const getclient = async (): Promise<ClientData[]> => {
    const config = useRuntimeConfig();
  try {
    const data = await $fetch<ClientData[]>(`${config.public.wpApiBase}/clients`);
    const filteredData = data.filter(collab => collab.acf.home_display)
    return filteredData ?? [];
  } catch (err) {
    console.error('Erreur lors de la récupération des clients:', err);
    return [];
  }
};

  /**
   * @description Récupère un client avec son ID 
   * @param id
   */
  const getClientById = async (id: string): Promise<ClientData | null> => {
    const config = useRuntimeConfig();
    try {
      const data = await $fetch<ClientData>(
        `${config.public.wpApiBase}/clients/${id}`
      );
      return data;
    } catch (err: any) {
      console.error("Erreur fetch client:", err);
      return null;
    }
  };
  
    const getContentAcf =async():Promise<SiteOptions | null> => {
    const config = useRuntimeConfig();
      try{
        const data = await $fetch<AcfOptions>(`${config.public.wpApiAcf}/options/options`,{
           cache: 'no-store',
        })
        return data.acf;
      }catch(error:any){
        console.error("Erreur fetch client:", error);
      return null;
      }
    }

  return {
    getProjects,
    getLastProjects,
    getCategories,
    getProjectById,
    getclient,
    getClientById,
    getContentAcf,
    getProjectsByCategory
  };
};